import React, { useEffect, useState, useCallback } from 'react';
import { Plane, RefreshCw, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sprint B · R5 — Riya Supplier Wallet widget.
 * Reads GET /api/admin/riya/balance (admin auth via cookie).
 * Auto-refreshes every 5 min.
 */
const RiyaBalanceCard = () => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBalance = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/admin/riya/balance${refresh ? '?refresh=true' : ''}`,
        { withCredentials: true }
      );
      setBalance(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Could not fetch balance');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    const t = setInterval(() => fetchBalance(true), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchBalance]);

  const theme = (() => {
    const bucket = balance?.bucket;
    if (bucket === 'red') {
      return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700',
        sub: 'text-red-600', icon: XCircle, label: 'Critical low' };
    }
    if (bucket === 'amber') {
      return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700',
        sub: 'text-amber-600', icon: AlertTriangle, label: 'Top up soon' };
    }
    return { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700',
      sub: 'text-sky-600', icon: CheckCircle, label: 'Healthy' };
  })();

  const Icon = theme.icon;

  return (
    <div
      className={`rounded-xl border ${theme.border} ${theme.bg} p-5 transition-shadow hover:shadow-md`}
      data-testid="riya-balance-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Plane className={`w-5 h-5 ${theme.text}`} />
          <h3 className="text-sm font-bold text-gray-800">
            Riya Supplier Wallet
            {balance?.mock && (
              <span className="ml-2 text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                MOCK
              </span>
            )}
          </h3>
        </div>
        <button
          onClick={() => fetchBalance(true)}
          disabled={loading}
          className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50 flex items-center gap-1"
          data-testid="riya-balance-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-100 rounded p-2 mb-2">
          {typeof error === 'string' ? error : 'Balance temporarily unavailable'}
        </div>
      )}

      {!balance && !error && (
        <div className="text-xs text-gray-500">Loading…</div>
      )}

      {balance && balance.success && (
        <>
          <div className="flex items-baseline gap-2 mb-1">
            <span className={`text-2xl font-bold ${theme.text}`}>
              ₹{(balance.wallet_balance || 0).toLocaleString('en-IN')}
            </span>
            <span className="text-xs text-gray-500">topup</span>
          </div>
          <div className="flex items-center gap-1 mb-3">
            <Icon className={`w-3.5 h-3.5 ${theme.sub}`} />
            <span className={`text-xs font-medium ${theme.sub}`}>{theme.label}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-3 border-t border-white/40">
            <div>
              <p className="text-[10px] uppercase text-gray-500 font-semibold">Credit</p>
              <p className="text-sm font-bold text-gray-800">₹{(balance.credit_balance || 0).toLocaleString('en-IN')}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-gray-500 font-semibold">Total</p>
              <p className="text-sm font-bold text-gray-800">₹{(balance.total_balance || 0).toLocaleString('en-IN')}</p>
            </div>
          </div>
          <div className="text-[10px] text-gray-400 mt-2">
            {balance.cached ? 'Cached (≤60s)' : 'Live'} · auto-refresh every 5 min
          </div>
        </>
      )}

      {balance && !balance.success && (
        <div className="text-xs text-gray-700">
          <p className="font-semibold">Balance unavailable</p>
          <p className="mt-1 text-gray-500">{balance.error || 'Riya /GetBalance returned an error.'}</p>
        </div>
      )}
    </div>
  );
};

export default RiyaBalanceCard;
